import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { 
  TrendingUp, 
  TrendingDown, 
  CheckCircle,
  Target,
  Award,
  ArrowRight,
  BarChart3,
  Minus,
  Eye
} from 'lucide-react';
import { VictoryChart, VictoryBar, VictoryAxis, VictoryTheme, VictoryLegend, VictoryGroup, VictoryPolarAxis, VictoryArea } from 'victory';

const API = process.env.REACT_APP_BACKEND_URL;

const AvaliacaoResultados = ({ pdiId, colaboradorId }) => {
  const [loading, setLoading] = useState(false);
  const [avaliacoes, setAvaliacoes] = useState([]);
  const [competencias, setCompetencias] = useState([]);
  const [loadingCompetencias, setLoadingCompetencias] = useState(false);
  
  // Gera um pdi_id baseado no colaborador se não existir
  const effectivePdiId = pdiId || `pdi_${colaboradorId}`;
  
  const [novaAvaliacao, setNovaAvaliacao] = useState({
    pdi_id: effectivePdiId,
    competencias_avaliadas: [],
    impacto_percebido: ''
  });

  useEffect(() => {
    if (colaboradorId) {
      carregarDiagnostico();
    }
    if (effectivePdiId) {
      loadAvaliacoes();
    }
  }, [effectivePdiId, colaboradorId]);

  // Atualiza o pdi_id quando o colaborador muda
  useEffect(() => {
    setNovaAvaliacao(prev => ({
      ...prev,
      pdi_id: effectivePdiId
    }));
  }, [effectivePdiId]);

  const carregarDiagnostico = async () => {
    try {
      setLoadingCompetencias(true);
      const response = await axios.get(
        `${API}/api/pdi-completo/diagnostico-ia/${colaboradorId}`,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      // Adiciona ID único para cada competência se não existir
      const competenciasData = (response.data.competencias_sugeridas || []).map((comp, index) => ({
        ...comp,
        id: comp.id || comp.competencia_id || `comp_${index}_${comp.competencia_nome?.replace(/\s+/g, '_')}`
      }));
      
      setCompetencias(competenciasData);
      
    } catch (error) {
      console.error('Erro ao carregar diagnóstico:', error);
      if (error.response?.status === 404) {
        toast.info('Execute o Diagnóstico IA primeiro para avaliar competências.');
      }
    } finally {
      setLoadingCompetencias(false);
    }
  };

  const loadAvaliacoes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/pdi-completo/avaliacoes/${effectivePdiId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      // O backend agora retorna { total, avaliacoes: [...] }
      setAvaliacoes(response.data.avaliacoes || []);
    } catch (error) {
      console.error('Erro ao carregar avaliações:', error);
      setAvaliacoes([]);
    }
  };

  const avaliarCompetencia = (competenciaId, nivelAtual) => {
    setNovaAvaliacao(prev => {
      const competenciasAvaliadas = [...prev.competencias_avaliadas];
      const index = competenciasAvaliadas.findIndex(c => c.competencia_id === competenciaId);
      
      // Converte escala 1-5 para 2-10 (multiplicando por 2)
      // Assim: 1→2, 2→4, 3→6, 4→8, 5→10
      const nivelConvertido = nivelAtual * 2;
      
      if (index >= 0) {
        // Competência já foi avaliada, atualiza o nível atual
        competenciasAvaliadas[index].nivel_atual = nivelConvertido;
      } else {
        // Busca a competência original no array de competências do diagnóstico
        const compOriginal = competencias.find(c => c.id === competenciaId);
        
        // Adiciona nova avaliação
        competenciasAvaliadas.push({
          competencia_id: competenciaId,
          nivel_inicial: compOriginal?.nivel_atual || compOriginal?.nivel_sugerido || 2,
          nivel_atual: nivelConvertido,
          nivel_desejado: compOriginal?.nivel_desejado || compOriginal?.nivel_sugerido || 10
        });
      }
      
      return { ...prev, competencias_avaliadas: competenciasAvaliadas };
    });
  };

  const salvarAvaliacao = async () => {
    try {
      if (novaAvaliacao.competencias_avaliadas.length === 0) {
        toast.error('Avalie pelo menos uma competência');
        return;
      }

      setLoading(true);

      // Obter o ID do usuário atual do token ou contexto
      const token = localStorage.getItem('token');
      const userId = JSON.parse(atob(token.split('.')[1])).sub; // Decodifica o JWT para pegar o user ID
      
      // Preparar dados da avaliação com todos os campos obrigatórios
      const avaliacaoData = {
        ...novaAvaliacao,
        colaborador_id: colaboradorId,
        avaliador_id: userId,
        tipo_avaliador: 'gestor' // ou 'auto' dependendo do contexto
      };

      const response = await axios.post(
        `${API}/api/pdi-completo/avaliar-resultados`, 
        avaliacaoData,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      toast.success('Avaliação salva com sucesso!');
      loadAvaliacoes();
      
      // Reset form
      setNovaAvaliacao({
        pdi_id: effectivePdiId,
        competencias_avaliadas: [],
        impacto_percebido: ''
      });

    } catch (error) {
      console.error('Erro ao salvar avaliação:', error);
      console.error('Detalhes do erro:', error.response?.data);
      toast.error(`Erro ao salvar avaliação: ${error.response?.data?.detail || 'Erro desconhecido'}`);
    } finally {
      setLoading(false);
    }
  };

  const calcularEvolucao = (inicial, atual, desejado) => {
    if (desejado === inicial) return 100;
    return Math.round(((atual - inicial) / (desejado - inicial)) * 100);
  };

  const getTrendIcon = (inicial, atual) => {
    if (atual > inicial) return <TrendingUp className="h-5 w-5 text-green-600" />;
    if (atual < inicial) return <TrendingDown className="h-5 w-5 text-red-600" />;
    return <Minus className="h-5 w-5 text-gray-600" />;
  };

  const renderGraficoRadar = (competenciasAvaliadas) => {
    if (!competenciasAvaliadas || competenciasAvaliadas.length === 0) return null;

    const dataInicial = competenciasAvaliadas.map(c => ({
      x: c.competencia_nome?.substring(0, 15) || 'Competência',
      y: c.nivel_inicial || 0
    }));

    const dataAtual = competenciasAvaliadas.map(c => ({
      x: c.competencia_nome?.substring(0, 15) || 'Competência',
      y: c.nivel_atual || 0
    }));

    return (
      <div style={{ width: '100%', height: 400 }}>
        <VictoryChart polar theme={VictoryTheme.material} domain={{ y: [0, 5] }}>
          <VictoryLegend
            x={125}
            y={10}
            orientation="horizontal"
            gutter={20}
            data={[
              { name: "Nível Inicial", symbol: { fill: "#fbbf24" } },
              { name: "Nível Atual", symbol: { fill: "#10b981" } }
            ]}
          />
          <VictoryGroup colorScale={["#fbbf24", "#10b981"]} style={{ data: { fillOpacity: 0.4, strokeWidth: 2 } }}>
            <VictoryArea data={dataInicial} />
            <VictoryArea data={dataAtual} />
          </VictoryGroup>
          <VictoryPolarAxis 
            tickValues={competenciasAvaliadas.map((_, i) => i)}
            labelPlacement="perpendicular"
            style={{ tickLabels: { fontSize: 8 } }}
          />
        </VictoryChart>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Nova Avaliação */}
      <Card className="border-2 border-purple-200">
        <CardHeader className="bg-gradient-to-r from-purple-50 to-blue-50">
          <CardTitle className="flex items-center space-x-2">
            <Target className="h-5 w-5 text-purple-600" />
            <span>Nova Avaliação de Resultados</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-6">
            {/* Avaliar Competências */}
            <div>
              <h4 className="font-semibold mb-4">Avaliar Competências (Nível Atual)</h4>
              
              {loadingCompetencias ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Carregando competências do diagnóstico...</p>
                </div>
              ) : competencias && competencias.length > 0 ? (
                <div className="space-y-4">
                  {competencias.map(comp => {
                    const avaliada = novaAvaliacao.competencias_avaliadas.find(
                      c => c.competencia_id === comp.id
                    );

                    return (
                      <Card key={comp.id}>
                        <CardContent className="p-4">
                          <div className="space-y-3">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h5 className="font-semibold">{comp.competencia_nome || comp.nome}</h5>
                                <div className="flex items-center space-x-4 text-sm text-gray-600 mt-2">
                                  <div>
                                    <span>Inicial:</span>
                                    <span className="ml-1 font-semibold text-yellow-600">
                                      {comp.nivel_atual?.toFixed(1) || comp.nivel_sugerido?.toFixed(1) || 'N/A'}
                                    </span>
                                  </div>
                                  <ArrowRight className="h-4 w-4" />
                                  <div>
                                    <span>Desejado:</span>
                                    <span className="ml-1 font-semibold text-green-600">
                                      {comp.nivel_desejado?.toFixed(1) || comp.nivel_sugerido?.toFixed(1) || 'N/A'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div>
                              <label className="text-sm font-semibold mb-2 block">
                                Nível Atual Alcançado:
                                <span className="ml-2 text-xs font-normal text-gray-500">
                                  (Escala 1-5, onde 1=2 pts e 5=10 pts)
                                </span>
                              </label>
                              <div className="flex space-x-2">
                                {[1, 2, 3, 4, 5].map(nivel => (
                                  <Button
                                    key={nivel}
                                    variant={avaliada?.nivel_atual === nivel * 2 ? 'default' : 'outline'}
                                    className={`flex-1 ${
                                      avaliada?.nivel_atual === nivel * 2
                                        ? 'bg-purple-600 hover:bg-purple-700' 
                                        : ''
                                    }`}
                                    onClick={() => avaliarCompetencia(comp.id, nivel)}
                                  >
                                    <div className="flex flex-col">
                                      <span className="text-lg font-bold">{nivel}</span>
                                      <span className="text-xs opacity-70">({nivel * 2})</span>
                                    </div>
                                  </Button>
                                ))}
                              </div>
                            </div>

                            {avaliada && (
                              <div className="pt-3 border-t">
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-gray-600">Evolução:</span>
                                  <div className="flex items-center space-x-2">
                                    {getTrendIcon(comp.nivel_atual || comp.nivel_sugerido, avaliada.nivel_atual)}
                                    <span className="font-semibold text-lg">
                                      {calcularEvolucao(
                                        comp.nivel_atual || comp.nivel_sugerido || 1, 
                                        avaliada.nivel_atual, 
                                        comp.nivel_desejado || comp.nivel_sugerido || 5
                                      )}%
                                    </span>
                                  </div>
                                </div>
                                <Progress 
                                  value={Math.min(100, calcularEvolucao(
                                    comp.nivel_atual || comp.nivel_sugerido || 1, 
                                    avaliada.nivel_atual, 
                                    comp.nivel_desejado || comp.nivel_sugerido || 5
                                  ))} 
                                  className="h-2 mt-2"
                                />
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12 bg-blue-50 rounded-lg border-2 border-blue-200">
                  <Target className="h-16 w-16 text-blue-300 mx-auto mb-4" />
                  <p className="text-lg font-semibold text-gray-700 mb-2">
                    Nenhum diagnóstico encontrado
                  </p>
                  <p className="text-sm text-gray-600 mb-4">
                    Execute o <strong>Diagnóstico IA</strong> primeiro para gerar competências a avaliar.
                  </p>
                  <Badge className="bg-blue-100 text-blue-800">
                    Acesse o módulo "Diagnóstico IA" para começar
                  </Badge>
                </div>
              )}
            </div>

            {/* Impacto Percebido */}
            <div>
              <label className="font-semibold mb-2 block">Impacto Percebido no Trabalho</label>
              <Textarea
                value={novaAvaliacao.impacto_percebido}
                onChange={(e) => setNovaAvaliacao({...novaAvaliacao, impacto_percebido: e.target.value})}
                placeholder="Descreva como a evolução nessas competências impactou seu trabalho..."
                rows={4}
              />
            </div>

            <Button 
              onClick={salvarAvaliacao} 
              disabled={loading || novaAvaliacao.competencias_avaliadas.length === 0}
              className="w-full bg-purple-600 hover:bg-purple-700"
            >
              {loading ? 'Salvando...' : 'Salvar Avaliação'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Histórico de Avaliações */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <BarChart3 className="h-5 w-5 text-blue-600" />
            <span>Histórico de Avaliações ({avaliacoes.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {avaliacoes.length === 0 ? (
            <div className="text-center py-12">
              <Eye className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Nenhuma avaliação realizada ainda</p>
            </div>
          ) : (
            <div className="space-y-6">
              {avaliacoes.map((aval, idx) => (
                <Card key={aval.id} className="border-2">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="text-sm text-gray-600">
                          Avaliação de {new Date(aval.data_avaliacao).toLocaleDateString('pt-BR')}
                        </div>
                        <div className="flex items-center space-x-2 mt-1">
                          <Badge className="bg-blue-100 text-blue-800">
                            {aval.tipo_avaliador}
                          </Badge>
                          {aval.status_conclusao && (
                            <Badge className="bg-green-100 text-green-800">
                              {aval.status_conclusao}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-600">Score de Evolução</div>
                        <div className="text-3xl font-bold text-purple-600">
                          {aval.score_evolucao_geral?.toFixed(1)}%
                        </div>
                      </div>
                    </div>

                    {/* Estatísticas */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="text-center p-3 bg-green-50 rounded-lg">
                        <div className="flex items-center justify-center mb-1">
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        </div>
                        <div className="text-2xl font-bold text-green-600">
                          {aval.competencias_atingidas || 0}
                        </div>
                        <div className="text-xs text-gray-600">Atingidas</div>
                      </div>
                      <div className="text-center p-3 bg-yellow-50 rounded-lg">
                        <div className="flex items-center justify-center mb-1">
                          <TrendingUp className="h-5 w-5 text-yellow-600" />
                        </div>
                        <div className="text-2xl font-bold text-yellow-600">
                          {aval.competencias_em_progresso || 0}
                        </div>
                        <div className="text-xs text-gray-600">Em Progresso</div>
                      </div>
                      <div className="text-center p-3 bg-red-50 rounded-lg">
                        <div className="flex items-center justify-center mb-1">
                          <Target className="h-5 w-5 text-red-600" />
                        </div>
                        <div className="text-2xl font-bold text-red-600">
                          {aval.competencias_nao_atingidas || 0}
                        </div>
                        <div className="text-xs text-gray-600">Não Atingidas</div>
                      </div>
                    </div>

                    {/* Gráfico Radar */}
                    {aval.competencias_avaliadas && aval.competencias_avaliadas.length > 0 && (
                      <div className="mb-6">
                        <h5 className="font-semibold mb-3">Comparativo Visual</h5>
                        {renderGraficoRadar(aval.competencias_avaliadas)}
                      </div>
                    )}

                    {/* Impacto */}
                    {aval.impacto_percebido && (
                      <div className="p-4 bg-purple-50 rounded-lg">
                        <div className="font-semibold mb-2">Impacto Percebido:</div>
                        <p className="text-sm text-gray-700">{aval.impacto_percebido}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AvaliacaoResultados;
